import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Link2, DollarSign, TrendingUp, Copy, Check, Loader2, ExternalLink, Wallet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { formatPrice, cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = '';

export default function AffiliatePage() {
  const { user, login } = useAuth();
  const [affiliateData, setAffiliateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    const fetchAffiliate = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/affiliate`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setAffiliateData(data);
        }
      } catch (error) {
        console.error('Error fetching affiliate:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliate();
  }, [user]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const response = await fetch(`${API_URL}/api/affiliate/join`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Bienvenue dans le programme affiliation!');
        // Refresh
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setJoining(false);
    }
  };

  const handleCopyLink = async () => {
    if (affiliateData?.referral_link) {
      await navigator.clipboard.writeText(affiliateData.referral_link);
      setCopied(true);
      toast.success('Lien copié!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 5000) {
      toast.error('Minimum de retrait: 5000 DA');
      return;
    }

    setWithdrawing(true);
    try {
      const response = await fetch(`${API_URL}/api/affiliate/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          amount,
          payment_method: 'bank_transfer'
        })
      });

      if (response.ok) {
        toast.success('Demande de retrait envoyée!');
        setShowWithdrawModal(false);
        setWithdrawAmount('');
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la demande');
    } finally {
      setWithdrawing(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Users size={48} className="text-[#D4AF37] mx-auto mb-4" />
          <h1 className="font-serif text-3xl mb-4">Programme Affiliation</h1>
          <p className="text-[#6B6A68] mb-6">
            Gagne de l'argent en recommandant Bloom Lingerie à tes amies!
          </p>
          <button 
            onClick={login}
            className="btn-primary px-6 py-3 text-sm font-medium"
            data-testid="login-btn"
          >
            Se connecter pour rejoindre
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <Loader2 className="animate-spin" size={32} />
      </div>
    );
  }

  // Not yet affiliate
  if (!affiliateData?.has_account) {
    return (
      <div className="min-h-screen bg-[#FCFBF9]" data-testid="affiliate-join-page">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="breadcrumb flex items-center gap-2 text-sm">
            <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
            <span>/</span>
            <span className="text-[#1A1514]">Programme Affiliation</span>
          </nav>
        </div>

        {/* Hero */}
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl mb-4">Deviens Affiliée Bloom</h1>
            <p className="text-xl text-[#6B6A68] max-w-2xl mx-auto">
              Recommande Bloom Lingerie et gagne 5% de commission sur chaque vente!
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6 bg-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#F4E3E1] rounded-full flex items-center justify-center">
                <DollarSign size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl mb-2">5% Commission</h3>
              <p className="text-sm text-[#6B6A68]">
                Gagne 5% sur chaque première commande de tes filleules
              </p>
            </div>
            <div className="text-center p-6 bg-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#F4E3E1] rounded-full flex items-center justify-center">
                <Link2 size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl mb-2">Lien Unique</h3>
              <p className="text-sm text-[#6B6A68]">
                Partage ton lien personnalisé sur les réseaux sociaux
              </p>
            </div>
            <div className="text-center p-6 bg-white">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#F4E3E1] rounded-full flex items-center justify-center">
                <Wallet size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl mb-2">Retrait Facile</h3>
              <p className="text-sm text-[#6B6A68]">
                Demande un virement à partir de 5000 DA
              </p>
            </div>
          </div>

          {/* Join Button */}
          <div className="text-center">
            <button
              onClick={handleJoin}
              disabled={joining}
              className="btn-primary px-12 py-4 text-sm font-medium uppercase tracking-wider disabled:opacity-50"
              data-testid="join-affiliate-btn"
            >
              {joining ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'Rejoindre le programme'}
            </button>
            <p className="text-xs text-[#6B6A68] mt-4">
              C'est gratuit et sans engagement
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Affiliate dashboard
  const affiliate = affiliateData.affiliate;

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="affiliate-dashboard">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">Mon Espace Affilié</span>
        </nav>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-serif text-3xl mb-8">Mon Espace Affilié</h1>

        {/* Referral Link */}
        <div className="bg-[#1A1514] text-white p-6 mb-8">
          <p className="text-sm text-gray-400 mb-2">Ton lien de parrainage</p>
          <div className="flex items-center gap-4">
            <code className="flex-1 bg-white/10 px-4 py-3 rounded text-sm truncate" data-testid="referral-link">
              {affiliateData.referral_link}
            </code>
            <button
              onClick={handleCopyLink}
              className={cn(
                "p-3 rounded transition-colors",
                copied ? "bg-[#A3B19B] text-white" : "bg-[#D4AF37] text-[#1A1514]"
              )}
              data-testid="copy-link-btn"
            >
              {copied ? <Check size={20} /> : <Copy size={20} />}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Code: <span className="text-[#D4AF37] font-mono">{affiliate.affiliate_code}</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 text-center">
            <p className="text-3xl font-serif text-[#D4AF37]">{affiliate.total_referrals}</p>
            <p className="text-sm text-[#6B6A68]">Filleules</p>
          </div>
          <div className="bg-white p-6 text-center">
            <p className="text-3xl font-serif text-[#D4AF37]">{affiliate.total_orders}</p>
            <p className="text-sm text-[#6B6A68]">Commandes</p>
          </div>
          <div className="bg-white p-6 text-center">
            <p className="text-3xl font-serif text-[#A3B19B]">{formatPrice(affiliate.pending_earnings)}</p>
            <p className="text-sm text-[#6B6A68]">En attente</p>
          </div>
          <div className="bg-white p-6 text-center">
            <p className="text-3xl font-serif">{formatPrice(affiliate.paid_earnings)}</p>
            <p className="text-sm text-[#6B6A68]">Payé</p>
          </div>
        </div>

        {/* Withdraw Button */}
        {affiliate.pending_earnings >= 5000 && (
          <div className="bg-[#F4E3E1] p-6 mb-8 flex items-center justify-between">
            <div>
              <p className="font-medium">Solde disponible</p>
              <p className="text-2xl font-serif text-[#D4AF37]">{formatPrice(affiliate.pending_earnings)}</p>
            </div>
            <button
              onClick={() => setShowWithdrawModal(true)}
              className="btn-primary px-6 py-3 text-sm font-medium"
              data-testid="withdraw-btn"
            >
              Demander un retrait
            </button>
          </div>
        )}

        {/* Recent Referrals */}
        <div className="bg-white p-6 mb-8">
          <h2 className="font-serif text-xl mb-6">Dernières conversions</h2>
          
          {affiliateData.referrals?.length > 0 ? (
            <div className="divide-y divide-[#E0DFDD]">
              {affiliateData.referrals.map((ref) => (
                <div key={ref.referral_id} className="py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{ref.referred_email || 'Client anonyme'}</p>
                    <p className="text-xs text-[#6B6A68]">
                      {new Date(ref.created_at).toLocaleDateString('fr-FR')} • 
                      Commande: {formatPrice(ref.order_amount)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={cn(
                      "font-medium",
                      ref.commission_status === 'pending' ? 'text-[#D4AF37]' :
                      ref.commission_status === 'paid' ? 'text-[#A3B19B]' :
                      'text-[#6B6A68]'
                    )}>
                      {ref.commission_amount > 0 ? `+${formatPrice(ref.commission_amount)}` : '-'}
                    </p>
                    <p className="text-xs text-[#6B6A68] capitalize">{ref.commission_status}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#6B6A68] text-center py-8">
              Aucune conversion pour le moment. Partage ton lien pour commencer!
            </p>
          )}
        </div>

        {/* Withdrawal History */}
        {affiliateData.withdrawals?.length > 0 && (
          <div className="bg-white p-6">
            <h2 className="font-serif text-xl mb-6">Historique des retraits</h2>
            <div className="divide-y divide-[#E0DFDD]">
              {affiliateData.withdrawals.map((w) => (
                <div key={w.withdrawal_id} className="py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{formatPrice(w.amount)}</p>
                    <p className="text-xs text-[#6B6A68]">
                      {new Date(w.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <span className={cn(
                    "text-xs px-2 py-1 rounded",
                    w.status === 'paid' ? 'bg-green-100 text-green-700' :
                    w.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    w.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                    'bg-red-100 text-red-700'
                  )}>
                    {w.status === 'paid' ? 'Payé' :
                     w.status === 'pending' ? 'En attente' :
                     w.status === 'approved' ? 'Approuvé' :
                     'Refusé'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="mt-8 p-6 bg-[#F4E3E1]">
          <h3 className="font-serif text-xl mb-4">Conseils pour réussir</h3>
          <ul className="space-y-2 text-sm text-[#6B6A68]">
            <li>• Partage ton lien sur Instagram et Facebook</li>
            <li>• Parle de tes produits préférés à tes amies</li>
            <li>• Crée du contenu autour de la lingerie et du bien-être</li>
            <li>• Tu gagnes une commission uniquement sur la première commande de chaque nouvelle cliente</li>
          </ul>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowWithdrawModal(false)} />
          <div className="relative bg-white p-8 max-w-md w-full">
            <h2 className="font-serif text-2xl mb-4">Demande de retrait</h2>
            <p className="text-sm text-[#6B6A68] mb-4">
              Solde disponible: {formatPrice(affiliate.pending_earnings)}
            </p>
            
            <div className="mb-4">
              <label className="text-sm text-[#6B6A68] block mb-2">Montant (DA)</label>
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                min="5000"
                max={affiliate.pending_earnings}
                placeholder="5000"
                className="checkout-input"
                data-testid="withdraw-amount"
              />
              <p className="text-xs text-[#6B6A68] mt-1">Minimum: 5000 DA</p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="flex-1 btn-secondary py-3 text-sm"
              >
                Annuler
              </button>
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                className="flex-1 btn-primary py-3 text-sm disabled:opacity-50"
                data-testid="confirm-withdraw-btn"
              >
                {withdrawing ? <Loader2 className="animate-spin mx-auto" size={18} /> : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
